import { loginAuthAPI } from "@/auth/api/loginAuthAPI";
import { queryClient } from "@/services/react-query";

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: string | null) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

/**
 * Validates if a token has the correct JWT structure (3 parts separated by dots)
 */
export const isValidTokenStructure = (token: string | null): boolean => {
  if (!token || typeof token !== "string") {
    return false;
  }

  // JWT should have 3 parts: header.payload.signature
  const parts = token.split(".");
  if (parts.length !== 3) {
    return false;
  }

  // Each part should be non-empty
  return parts.every((part) => part.length > 0);
};

/**
 * Decodes JWT token to extract payload
 * Returns null if token is invalid or corrupted
 */
const jwtDecode = (token: string) => {
  try {
    // Validate structure first
    if (!isValidTokenStructure(token)) {
      return null;
    }

    const base64Url = token.split(".")[1];
    if (!base64Url) {
      return null;
    }

    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split("")
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join(""),
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
};

/**
 * Checks if a token is valid (well-formed and not expired)
 * @returns true if token is valid, false if corrupted, expired, or invalid
 */
export const isTokenValid = (token: string | null): boolean => {
  if (!token) {
    return false;
  }

  // Check structure
  if (!isValidTokenStructure(token)) {
    return false;
  }

  try {
    const decoded = jwtDecode(token);

    if (!decoded || !decoded.exp) {
      return false;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    const expirationTime = decoded.exp;

    // Token is valid if it hasn't expired yet
    return expirationTime > currentTime;
  } catch (error) {
    return false;
  }
};

/**
 * Checks if the access token is expired or will expire within the specified buffer time
 * Also validates token structure - returns true if token is corrupted/invalid
 * @param bufferSeconds - Number of seconds before expiration to consider token as expiring (default: 300 = 5 minutes)
 * @returns true if token is expired, expiring soon, or invalid/corrupted, false otherwise
 */
export const isTokenExpiredOrExpiringSoon = (
  bufferSeconds: number = 300,
): boolean => {
  const accessToken = localStorage.getItem("access_token");

  if (!accessToken) {
    return true;
  }

  // Check if token structure is valid
  if (!isValidTokenStructure(accessToken)) {
    return true; // Token is corrupted/invalid
  }

  try {
    const decoded = jwtDecode(accessToken);

    // If decoding failed or token doesn't have expiration, consider it invalid
    if (!decoded || !decoded.exp) {
      return true;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    const expirationTime = decoded.exp;
    const timeUntilExpiry = expirationTime - currentTime;

    // Token is expired or will expire within buffer time
    return timeUntilExpiry <= bufferSeconds;
  } catch (error) {
    // Token is corrupted or invalid
    return true;
  }
};

/**
 * Clears all authentication data from localStorage and React Query cache.
 * Ensures a new user never sees the previous user's cached data (e.g. dashboard).
 */
export const clearAuthData = (): void => {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("user");
  queryClient.clear();
};

/**
 * Performs a full logout - clears auth data and redirects to login
 */
export const logout = (): void => {
  clearAuthData();
  window.location.href = "/auth/one-id";
};

export const refreshAccessToken = async (): Promise<string | null> => {
  const refreshToken = localStorage.getItem("refresh_token");

  if (!refreshToken) {
    return null;
  }

  // If already refreshing, queue this request
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    }) as Promise<string | null>;
  }

  isRefreshing = true;

  try {
    const response = await loginAuthAPI.refreshToken({
      refresh: refreshToken,
    });

    // Validate response
    if (!response || !response.access) {
      throw new Error("Invalid refresh token response");
    }

    // Update access token
    localStorage.setItem("access_token", response.access);

    // Update refresh token only if a new one is provided
    // Some backends return a new refresh token, others don't
    // If not provided, we keep the existing one
    if (response.refresh && isValidTokenStructure(response.refresh)) {
      localStorage.setItem("refresh_token", response.refresh);
    }
    // Note: If no new refresh token is provided, we keep the old one in localStorage

    // Process all queued requests with the new token
    processQueue(null, response.access);
    isRefreshing = false;

    return response.access;
  } catch (error) {
    // Process all queued requests with the error
    processQueue(error as Error, null);
    isRefreshing = false;

    // Clear auth data on refresh failure
    clearAuthData();

    return null;
  }
};
